import React from "react";
import { MenuItem, MenuList } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridCellCheckboxRenderer } from "@mui/x-data-grid";
import {
	CustomButton,
	CustomMenuButton,
	CustomChips,
	CustomTooltip,
	icon,
	IconComponent,
	KnowledgeBasePopOver,
	TextMidCut,
} from "../../../../common/components";
import { FILE_TYPES } from "../../../../common/constants/file-types";
import { isSegFeatureEnabled, SegFlags, translate, translateDate } from "../../../../common/providers";
import { compareInsensitiveCaseString, isNumber, matchTypeIcon, removeTags } from "../../../../common/utils";
import { DOCUMENT_STATUS } from "../../constants/constants";
import PastilleScore from "./PastilleScore";
import styles from "./DocumentCenterTable.module.css";
import ManageColumnsButton from "./ManageColumnsButton";
import { checkValidity } from "../../../../navigation";
import DocumentTableRowStatus from "./DocumentTableRowStatus";
import DocumentTableRowMenu from "./DocumentTableRowMenu";
import EffectiveDateCell from "./EffectiveDateCell";

const getScoreColor = (parsingScore) => {
	if (!isNumber(parsingScore)) {
		return null;
	}
	if (parsingScore < 30) {
		return "red";
	}
	if (parsingScore >= 85) {
		return "green";
	}
	return "orange";
};

const displayStatusText = (status) => {
	switch (status) {
		case DOCUMENT_STATUS.IN_PROGRESS:
			return <span>{translate("document-center.table.row.status.in-progress")}</span>;
		case DOCUMENT_STATUS.TIMEOUT:
		case DOCUMENT_STATUS.FAILED:
			return (
				<CustomTooltip title={translate(`document-center.table.row.status.${status.toLowerCase()}`)}>
					<IconComponent color="var(--red-500)" icon={icon.faExclamationTriangle} />
				</CustomTooltip>
			);
		case DOCUMENT_STATUS.PENDING:
			return <span>{translate(`document-center.table.row.status.${status.toLowerCase()}`)}</span>;
		default:
			return null;
	}
};

const checkboxColumn = {
	...GRID_CHECKBOX_SELECTION_COL_DEF,
	renderCell: (params) => <GridCellCheckboxRenderer {...params} data-testid={`doc_${params.row.id}`} />,
};

const fileNameColumn = {
	field: "FILENAME",
	disableColumnMenu: true,
	headerClassName: styles.headerCell,
	display: "flex",
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.name")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.name")}</div>
		</CustomTooltip>
	),
	flex: 1,
	minWidth: 240,
	renderCell: ({ row }) => {
		const isUnsupported =
			compareInsensitiveCaseString(row.type, FILE_TYPES.UNSUPPORTED) ||
			[DOCUMENT_STATUS.FAILED, DOCUMENT_STATUS.NOT_MANAGED].includes(row.status);
		return (
			<div className={styles.table__label}>
				<div
					className={`${styles["table__label--leftSide"]} ${
						(row.type === "folder" || row.docType !== "DELIVERABLE") && styles["table__label--fullSize"]
					} ${isUnsupported && styles["table__label--unSupported"]}`}
				>
					<div>
						<IconComponent
							className={styles.label__icon}
							data-type={row.type}
							icon={icon[matchTypeIcon({ type: row.type.toLowerCase() })]}
							size="md"
						/>
					</div>
					<span className={styles.label__textContainer}>
						<TextMidCut
							tooltip
							autoTranslate="no"
							className={styles.label__text}
							data-testid="doc.label"
							text={row.label}
							tooltipTitle={
								<div>
									<b>{row.label}</b>
									{row.label !== row.filename && <div>{row.filename}</div>}
								</div>
							}
						/>
						{row.label !== row.filename && (
							<TextMidCut autoTranslate="no" className={styles.label__subText} text={row.filename} />
						)}
					</span>
				</div>
				{isSegFeatureEnabled(SegFlags.DOC_TYPE_DELIVERABLE) && row.docType === "DELIVERABLE" && (
					<CustomChips
						className={styles.label__chip}
						color="info"
						label={translate("document-center.table.actions.type.deliverable")}
						size="sm"
					/>
				)}
			</div>
		);
	},
};

const getVersionColumn = ({ hasActionPermission, onInitBulkVersioning }) => ({
	field: "VERSION",
	headerName: translate("document-center.table.column.version"),
	display: "flex",
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.version")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.version")}</div>
		</CustomTooltip>
	),
	width: 120,
	minWidth: 70,
	disableColumnMenu: true,
	renderCell: ({ row }) => {
		const isBulkVersioningAllowed =
			isSegFeatureEnabled(SegFlags.VERSIONS_BULK) && row.status === "CANDIDATE_VERSIONS" && row.type !== "folder";
		if (!isBulkVersioningAllowed) {
			return row.version;
		}
		return (
			<CustomTooltip
				title={hasActionPermission ? "" : translate("document-center.table.row.to-validate.disabled")}
			>
				<span>
					<CustomButton
						disabled={!hasActionPermission}
						size="xs"
						onClick={(e) => {
							e.stopPropagation();
							onInitBulkVersioning(row);
						}}
					>
						{translate("document-center.table.row.to-validate")}
					</CustomButton>
				</span>
			</CustomTooltip>
		);
	},
});

const updatedColumn = {
	field: "UPDATED",
	width: 140,
	headerClassName: styles.headerCell,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.updated")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.updated")}</div>
		</CustomTooltip>
	),
	disableColumnMenu: true,
	renderCell: ({ row }) => translateDate(row.updated),
};

const pagesColumn = {
	field: "PAGES",
	width: 100,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.pages")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.pages")}</div>
		</CustomTooltip>
	),
	headerClassName: styles.headerCell,
	disableColumnMenu: true,
	display: "flex",
	renderCell: ({ row }) =>
		(![DOCUMENT_STATUS.FAILED].includes(row.status) && row.pages > 0 && row.type !== "folder" && row.pages) || "",
};

const requirementsColumn = {
	field: "REQUIREMENT",
	width: 130,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.requirements")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.requirements")}</div>
		</CustomTooltip>
	),
	headerClassName: styles.headerCell,
	disableColumnMenu: true,
	display: "flex",
	renderCell: ({ row }) => {
		if (!isNumber(row?.reqs) || row.type === "folder") {
			return null;
		}
		return row.reqs > 0 ? row.reqs : "-";
	},
};

const qualityColumn = {
	field: "QUALITY",
	headerClassName: styles.headerCell,
	width: 130,
	minWidth: 84,
	disableColumnMenu: true,
	headerName: translate("document-center.table.column.quality"),
	display: "flex",
	sortable: false,
	renderHeader: () => (
		<div className={styles.innerCell}>
			{translate("document-center.table.column.quality")}
			<KnowledgeBasePopOver
				color="var(--icon-secondary)"
				label={translate("document-center.table.column.quality.pop-up.information")}
			/>
		</div>
	),
	renderCell: ({ row }) => {
		if (
			compareInsensitiveCaseString(row.type, FILE_TYPES.FOLDER) ||
			compareInsensitiveCaseString(row.type, FILE_TYPES.UNSUPPORTED)
		) {
			return null;
		}
		const scoreColor = getScoreColor(row.parsing_score);
		return scoreColor ? (
			<PastilleScore color={scoreColor} />
		) : (
			<div className={styles.status}>{displayStatusText(row.status)}</div>
		);
	},
};

const numberColumn = {
	field: "Numéro",
	disableColumnMenu: true,
	headerClassName: styles.headerCell,
	width: 130,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.number")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.number")}</div>
		</CustomTooltip>
	),
	sortable: false,
	renderCell: ({ row }) => (
		<div className={styles["innerCell--veolia"]}>
			{row?.documentAttributes?.find((attr) => attr.attributeKey === "Numéro")?.attributeValue}
		</div>
	),
};
const effectiveDateColumn = {
	field: "Date d'effet du texte",
	headerClassName: styles.headerCell,
	width: 130,
	disableColumnMenu: true,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.effective-date")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.effective-date")}</div>
		</CustomTooltip>
	),
	sortable: false,
	renderCell: ({ row }) => <EffectiveDateCell row={row} />,
};
const natureColumn = {
	field: "Nature du texte contractuel",
	disableColumnMenu: true,
	headerClassName: styles.headerCell,
	width: 130,
	sortable: false,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.nature")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.nature")}</div>
		</CustomTooltip>
	),
	renderCell: ({ row }) => (
		<CustomTooltip
			showWhenOverflow
			title={
				row?.documentAttributes?.find((attr) => attr.attributeKey === "Nature du texte contractuel")
					?.attributeValue
			}
		>
			<div className={styles["innerCell--veolia"]}>
				{removeTags(
					row?.documentAttributes?.find((attr) => attr.attributeKey === "Nature du texte contractuel")
						?.attributeValue
				)}
			</div>
		</CustomTooltip>
	),
};
const textTypeColumn = {
	field: "Type de texte",
	disableColumnMenu: true,
	width: 130,
	headerClassName: styles.headerCell,
	sortable: false,
	minWidth: 70,
	renderHeader: () => (
		<CustomTooltip showWhenOverflow title={translate("document-center.table.column.type-of-text")}>
			<div className={styles.headerCell}>{translate("document-center.table.column.type-of-text")}</div>
		</CustomTooltip>
	),
	renderCell: ({ row }) => (
		<CustomTooltip
			showWhenOverflow
			title={row?.documentAttributes?.find((attr) => attr.attributeKey === "Type de texte")?.attributeValue}
		>
			<div className={styles["innerCell--veolia"]}>
				{row?.documentAttributes?.find((attr) => attr.attributeKey === "Type de texte")?.attributeValue}
			</div>
		</CustomTooltip>
	),
};
const actionsColumn = ({ onOpenSP, onDelete, onOpenDownloadDialog, onOpenMoveDialog, onOpenVersionDialog }) => {
	const handleOpenSP = (e, row) => {
		e?.stopPropagation();
		onOpenSP(row.id, row.type, row.status);
	};
	return {
		field: "actions",
		disableColumnMenu: true,
		width: 76,
		headerClassName: styles.actionsHeaderCellWrapper,
		resizable: false,
		align: "center",
		headerAlign: "center",
		display: "flex",
		resizeble: false,
		sortable: false,
		renderCell: ({ row }) => {
			const isBulkVersioningAllowed =
				isSegFeatureEnabled(SegFlags.VERSIONS_BULK) &&
				row.status === "CANDIDATE_VERSIONS" &&
				row.type !== "folder";
			return (
				<div className={styles.actionButton}>
					{![DOCUMENT_STATUS.IN_PROGRESS, DOCUMENT_STATUS.FAILED, DOCUMENT_STATUS.TIMEOUT].includes(
						row.status
					) &&
						!isBulkVersioningAllowed && (
							<DocumentTableRowMenu
								document={row}
								documentId={row?.id}
								documentType={row?.type}
								onOpenDownloadDialog={onOpenDownloadDialog}
								onOpenMoveDialog={onOpenMoveDialog}
								onOpenSidPanel={handleOpenSP}
								onOpenVersionDialog={onOpenVersionDialog}
							/>
						)}
					{[DOCUMENT_STATUS.IN_PROGRESS, DOCUMENT_STATUS.FAILED, DOCUMENT_STATUS.TIMEOUT].includes(
						row.status
					) && (
						<div className={styles.cellActions}>
							<DocumentTableRowStatus
								docId={row.id}
								docStatus={row?.status}
								progression={row?.progression}
								onDelete={onDelete}
							/>
							{!isBulkVersioningAllowed && (
								<CustomMenuButton icon={icon.faEllipsisV} size="sm" variant="text">
									<MenuList>
										<MenuItem onClick={(e) => handleOpenSP(e, row)}>
											{translate("document-center.table.row.menu-item.display-details")}
										</MenuItem>
									</MenuList>
								</CustomMenuButton>
							)}
						</div>
					)}
				</div>
			);
		},
		renderHeader: () => <ManageColumnsButton />,
	};
};

export default function getDocumentColumns({
	columnOrder,
	hasActionPermission,
	onInitBulkVersioning,
	onOpenSP,
	onDelete,
	onOpenDownloadDialog,
	onOpenMoveDialog,
	onOpenVersionDialog,
}) {
	let finalColumns = [checkboxColumn, fileNameColumn];
	finalColumns = [
		...finalColumns,
		...columnOrder
			.filter((colOrder) => colOrder.visibility && checkValidity(colOrder))
			.map((colOrder) =>
				[
					getVersionColumn({ hasActionPermission, onInitBulkVersioning }),
					updatedColumn,
					pagesColumn,
					requirementsColumn,
					qualityColumn,
					numberColumn,
					effectiveDateColumn,
					natureColumn,
					textTypeColumn,
				].find((col) => col.field === colOrder.key)
			),
	];
	finalColumns = [
		...finalColumns,
		actionsColumn({ onOpenSP, onDelete, onOpenDownloadDialog, onOpenMoveDialog, onOpenVersionDialog }),
	];
	return finalColumns;
}
